import type { ComponentType, World } from "./world.js";

// A deterministic state hash: the same values give the same 64-bit hex digest on every run, in every page and in
// Node, so a replay or a restored save can be checked against a recorded run by comparing one string.
//
// Rules:
// - Numbers hash by their exact float64 bits (a change in the last bit changes the hash), except that -0 hashes as 0
//   and every NaN as one NaN: a JSON save cannot keep either distinction, so a restored state must not differ by them.
// - Objects hash by sorted keys (key insertion order never matters); arrays and typed arrays in order; `undefined`
//   object fields are skipped, as JSON.stringify skips them.
// - Two lanes of MurmurHash3's 32-bit mixing with different seeds, over a stream of 32-bit words with a type tag
//   before each value, so `[1, 2]` and `[12]` or `"1"` and `1` never collide by construction.

const F64 = new Float64Array(1);
const U32 = new Uint32Array(F64.buffer);

const Tag = { Null: 1, False: 2, True: 3, Num: 4, Str: 5, Arr: 6, Obj: 7, Bytes: 8 } as const;

function mix(h: number, k: number): number {
  k = Math.imul(k, 0xcc9e2d51);
  k = (k << 15) | (k >>> 17);
  k = Math.imul(k, 0x1b873593);
  h ^= k;
  h = (h << 13) | (h >>> 19);
  return (Math.imul(h, 5) + 0xe6546b64) | 0;
}

function fmix(h: number, len: number): number {
  h ^= len;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** An incremental state hasher: feed values in a fixed order, then read `digest()`. */
export class StateHasher {
  private a = 0x9747b28c | 0;
  private b = 0x5bd1e995 | 0;
  private n = 0;

  private word(w: number): void {
    this.a = mix(this.a, w | 0);
    this.b = mix(this.b, (w ^ 0x2545f491) | 0);
    this.n++;
  }

  /** A number, by its float64 bits (-0 as 0, every NaN as one NaN). */
  num(x: number): this {
    this.word(Tag.Num);
    F64[0] = x === 0 ? 0 : Number.isNaN(x) ? NaN : x;
    this.word(U32[0]!);
    this.word(U32[1]!);
    return this;
  }

  /** A string (its UTF-16 code units and length). */
  str(s: string): this {
    this.word(Tag.Str);
    this.word(s.length);
    for (let i = 0; i < s.length; i++) this.word(s.charCodeAt(i));
    return this;
  }

  /** Raw bytes (a snapshot, a typed array's buffer view). */
  bytes(u8: Uint8Array): this {
    this.word(Tag.Bytes);
    this.word(u8.length);
    for (let i = 0; i < u8.length; i++) this.word(u8[i]!);
    return this;
  }

  /** Any JSON-like value: null, booleans, numbers, strings, arrays, typed arrays and plain objects (keys sorted). */
  value(v: unknown): this {
    if (v === null || v === undefined) this.word(Tag.Null);
    else if (typeof v === "boolean") this.word(v ? Tag.True : Tag.False);
    else if (typeof v === "number") this.num(v);
    else if (typeof v === "string") this.str(v);
    else if (Array.isArray(v)) {
      this.word(Tag.Arr);
      this.word(v.length);
      for (const x of v) this.value(x);
    } else if (ArrayBuffer.isView(v)) {
      const arr = v as unknown as ArrayLike<number>;
      this.word(Tag.Arr);
      this.word(arr.length);
      for (let i = 0; i < arr.length; i++) this.num(arr[i]!);
    } else if (typeof v === "object") {
      const o = v as Record<string, unknown>;
      const keys = Object.keys(o).filter((k) => o[k] !== undefined).sort();
      this.word(Tag.Obj);
      this.word(keys.length);
      for (const k of keys) {
        this.str(k);
        this.value(o[k]);
      }
    } else throw new Error(`hashState: cannot hash a ${typeof v}`);
    return this;
  }

  /** The 64-bit digest as 16 hex digits. Feeding more after reading it is allowed (the digest covers all so far). */
  digest(): string {
    const hex = (x: number): string => x.toString(16).padStart(8, "0");
    return hex(fmix(this.a, this.n)) + hex(fmix(this.b, this.n * 31));
  }
}

/** The deterministic hash of one JSON-like value (see StateHasher for the rules). */
export function hashState(v: unknown): string {
  return new StateHasher().value(v).digest();
}

/** The hash of these component types across a world: per type, every (entity, data) pair in entity-id order. */
export function hashWorld(world: World, types: readonly ComponentType[]): string {
  const h = new StateHasher();
  for (const t of types) {
    h.str(t);
    const entries = world.componentEntries<unknown>(t).sort((x, y) => x[0] - y[0]);
    h.num(entries.length);
    for (const [id, data] of entries) h.num(id).value(data);
  }
  return h.digest();
}
